// Settings > Access: "Who can fill this form" and anonymous responses.
// Everything is enforced on the server (get-published-form, submit-form);
// this screen only edits the dform_audience row for the form.

import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import CheckboxSelect from "./CheckboxSelect";
import { apiFetch } from "./utils/api";
import "./WhoCanFill.css";

const SMALL_AUDIENCE = 5;

const toDeptOption = d => ({ value: String(d.id), label: d.name });
const toPersonOption = p => ({
    value: p.emp_id,
    label: `${p.name} (${p.emp_id})`,
    detail: [p.department, p.location].filter(Boolean).join(", "),
});

// "Who can fill" choices, as in dAdmin's Holiday screen: pick a type, then
// tick values in its list. A form can still combine types (anyone who
// matches any of them can fill it): values of the other types stay and are
// shown under the list.
const WHO_OPTIONS = [
    { value: "all", label: "All employees" },
    { value: "department", label: "Department" },
    { value: "location", label: "Location" },
    { value: "employee", label: "Employee" },
];
const PICK_LABEL = { department: "Select department", location: "Select location", employee: "Select employee" };
const TYPE_NAME = { department: "Department", location: "Location", employee: "Employee" };

const WhoCanFill = ({ formId }) => {
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState("all");
    const [departments, setDepartments] = useState([]);
    const [locations, setLocations] = useState([]);
    const [people, setPeople] = useState([]);
    const [anonymous, setAnonymous] = useState(false);
    const [responseCount, setResponseCount] = useState(0);
    const [canChangeAnonymous, setCanChangeAnonymous] = useState(true);
    const [deptOptions, setDeptOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [peopleOptions, setPeopleOptions] = useState([]);
    const [pickBy, setPickBy] = useState("department");
    const [preview, setPreview] = useState(null); // { count, no_access, no_access_count }
    const [savedKey, setSavedKey] = useState("");

    const draft = useMemo(() => ({
        mode,
        departments: departments.map(d => d.value),
        locations: locations.map(l => l.value),
        emp_ids: people.map(p => p.value),
        anonymous,
    }), [mode, departments, locations, people, anonymous]);
    const draftKey = JSON.stringify(draft);
    const changed = loaded && draftKey !== savedKey;

    // Load the current settings and the pick lists.
    useEffect(() => {
        if (!formId) return;
        let cancelled = false;
        (async () => {
            try {
                const [aRes, dRes, lRes, pRes] = await Promise.all([
                    apiFetch(`/api/audience/${formId}`),
                    apiFetch("/api/directory/departments"),
                    apiFetch("/api/directory/locations"),
                    apiFetch("/api/directory/employees?all=1"),
                ]);
                if (!aRes.ok) throw new Error("Could not load who can fill this form.");
                const a = await aRes.json();
                const depts = dRes.ok ? await dRes.json() : [];
                const locs = lRes.ok ? await lRes.json() : [];
                const everyone = pRes.ok ? await pRes.json() : [];
                if (cancelled) return;
                const selectedDepts = a.departments.map(toDeptOption);
                const selectedLocs = a.locations.map(l => ({ value: l, label: l }));
                const selectedPeople = a.people.map(toPersonOption);
                setDeptOptions(depts.map(toDeptOption));
                setLocationOptions(locs.map(l => ({ value: l, label: l })));
                setPeopleOptions(everyone.map(toPersonOption));
                // Show the list of the first type that has values.
                setPickBy(selectedDepts.length ? "department" : selectedLocs.length ? "location" : selectedPeople.length ? "employee" : "department");
                setMode(a.mode);
                setDepartments(selectedDepts);
                setLocations(selectedLocs);
                setPeople(selectedPeople);
                setAnonymous(!!a.anonymous);
                setResponseCount(a.response_count || 0);
                setCanChangeAnonymous(!!a.can_change_anonymous);
                setSavedKey(JSON.stringify({
                    mode: a.mode,
                    departments: selectedDepts.map(d => d.value),
                    locations: selectedLocs.map(l => l.value),
                    emp_ids: selectedPeople.map(p => p.value),
                    anonymous: !!a.anonymous,
                }));
                setLoaded(true);
            } catch (err) {
                if (!cancelled) Swal.fire("Error", err.message, "error");
            }
        })();
        return () => { cancelled = true; };
    }, [formId]);

    // Live count for the current (possibly unsaved) choice.
    useEffect(() => {
        if (!loaded) return undefined;
        const timer = setTimeout(async () => {
            try {
                const res = await apiFetch(`/api/audience/${formId}/preview`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(draft),
                });
                if (res.ok) setPreview(await res.json());
            } catch (e) { /* the count is only a hint */ }
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, formId, draftKey]);

    const save = async () => {
        setSaving(true);
        try {
            const res = await apiFetch(`/api/audience/${formId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(draft),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                Swal.fire("Not saved", data.error || "Could not save who can fill this form.", "warning");
                return;
            }
            setSavedKey(draftKey);
            Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Saved", showConfirmButton: false, timer: 1800 });
        } catch (err) {
            Swal.fire("Error", "Could not save who can fill this form.", "error");
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) return <div className="wcf-card wcf-loading">Loading who can fill this form...</div>;

    const count = preview ? preview.count : null;
    const lists = {
        department: { options: deptOptions, value: departments, set: setDepartments },
        location: { options: locationOptions, value: locations, set: setLocations },
        employee: { options: peopleOptions, value: people, set: setPeople },
    };
    // Values of the types not shown in the list right now.
    const others = mode === "restricted"
        ? Object.keys(lists).filter(t => t !== pickBy).flatMap(t => lists[t].value.map(item => ({ type: t, item })))
        : [];
    const restrictedEmpty = mode === "restricted" && !departments.length && !locations.length && !people.length;

    return (
        <div className="wcf-card">
            <div className="wcf-title">Who can fill this form</div>

            <div className="wcf-pickers">
                <label className="wcf-label" htmlFor="wcf-who">Who can fill</label>
                <Select inputId="wcf-who" options={WHO_OPTIONS} isSearchable={false} classNamePrefix="wcf-select"
                    value={WHO_OPTIONS.find(o => o.value === (mode === "all" ? "all" : pickBy))}
                    onChange={o => {
                        if (o.value === "all") { setMode("all"); return; }
                        setMode("restricted");
                        setPickBy(o.value);
                    }} />

                {mode === "restricted" && (
                    <>
                        <label className="wcf-label" htmlFor="wcf-pick">{PICK_LABEL[pickBy]}</label>
                        <CheckboxSelect inputId="wcf-pick" key={pickBy}
                            options={lists[pickBy].options} value={lists[pickBy].value} onChange={v => lists[pickBy].set(v ? [...v] : [])}
                            placeholder={pickBy === "employee" ? "Search by name or employee id" : "Select..."}
                            noOptionsMessage={() => "Nothing found"} />
                        <p className="wcf-help">Anyone who matches any of the chosen values can fill the form.</p>
                        {others.length > 0 && (
                            <div className="wcf-also">
                                <span className="wcf-also-title">Also included:</span>
                                {others.map(({ type, item }) => (
                                    <span className="wcf-chip" key={`${type}-${item.value}`}>
                                        <span className="wcf-chip-type">{TYPE_NAME[type]}:</span> {item.label}
                                        <button type="button" aria-label={`Remove ${item.label}`}
                                            onClick={() => lists[type].set(lists[type].value.filter(x => x.value !== item.value))}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="wcf-count" aria-live="polite">
                {restrictedEmpty
                    ? "Choose at least one department, location or employee. Until then nobody can fill this form."
                    : count === null ? "Counting..." : `${count} employee${count === 1 ? "" : "s"} can fill this form`}
            </div>

            {preview && preview.no_access_count > 0 && (
                <div className="wcf-warning">
                    {preview.no_access_count} of them {preview.no_access_count === 1 ? "doesn't" : "don't"} have dForms access, so they can't sign in to respond:{" "}
                    {preview.no_access.map(p => p.name).join(", ")}
                    {preview.no_access_count > preview.no_access.length ? ` and ${preview.no_access_count - preview.no_access.length} more` : ""}.
                </div>
            )}

            <div className="wcf-anon">
                <div className="wcf-anon-text">
                    <strong>Anonymous responses</strong>
                    <p>Names and exact times are hidden from you. We still track who has responded so reminders work.</p>
                    {mode === "restricted" && count !== null && count > 0 && count < SMALL_AUDIENCE && (
                        <div className="wcf-warning">Small audience: answers may still be identifiable.</div>
                    )}
                    {!canChangeAnonymous && (
                        <div className="wcf-note">
                            This can only be changed while the form has no responses (it has {responseCount}).
                            Switching it on now would not hide names already collected, and switching it off would
                            reveal people who answered expecting to stay anonymous.
                        </div>
                    )}
                </div>
                <label className="switch">
                    <input type="checkbox" checked={anonymous} disabled={!canChangeAnonymous}
                        onChange={e => setAnonymous(e.target.checked)} aria-label="Anonymous responses" />
                    <span className="slider round"></span>
                </label>
            </div>

            <div className="wcf-actions">
                <button type="button" className="save-btn" disabled={!changed || saving} onClick={save}>
                    {saving ? "Saving..." : "Save"}
                </button>
                {changed && <span className="wcf-unsaved">Unsaved changes</span>}
            </div>
        </div>
    );
};

export default WhoCanFill;
